import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/settings.css";
import profilePlaceholder from "../assets/placeholder.png";
import { MdLogout } from "react-icons/md"; 
import { useNavigate } from "react-router-dom"; 
import cameraIcon from "../assets/camera.png";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("personal");
  const [profileImg, setProfileImg] = useState(profilePlaceholder);
  const [loading, setLoading] = useState(false);

  // ✅ Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const navigate = useNavigate();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    phone: "",
    gender: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const CLOUD_NAME = "dlmrcsaqf";
  const UPLOAD_PRESET = "unsigned_preset";

  const getUserId = () => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      return parsedUser._id || parsedUser.id || null;
    }
    return null;
  };

  // ✅ Show modal helper
  const showAppModal = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const handleLogoutConfirm = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/", { replace: true });
  };

  useEffect(() => {
    const userId = getUserId();
    if (!userId) return showAppModal("No userId found in localStorage");

    const fetchUser = async () => {
      try {
        const res = await axios.get(`https://firetrace-capstone.onrender.com/api/users/${userId}`);
        const user = res.data;
        setFormData({
          name: user.name || "",
          email: user.email || "",
          address: user.address || "",
          phone: user.phone || "",
          gender: user.gender || "",
        });
        setProfileImg(user.profileImage || profilePlaceholder);
      } catch (err) {
        console.error("Error fetching user:", err);
        showAppModal("Failed to fetch user data.");
      }
    };

    fetchUser();
  }, []);

const handleChange = (e) => {
  const { name, value } = e.target;

  // ✅ Validate name: letters and spaces only
  if (name === "name") {
    const lettersOnly = /^[A-Za-z\s]*$/;
    if (!lettersOnly.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    return;
  }

  // ✅ Validate contact: numbers only, max 11 digits
  if (name === "contact") {
    const numbersOnly = value.replace(/[^0-9]/g, ""); // only digits
    if (numbersOnly.length > 11) return; // stop at 11
    setFormData((prev) => ({ ...prev, [name]: numbersOnly }));
    return;
  }

  // ✅ Default handler for other fields
  setFormData((prev) => ({ ...prev, [name]: value }));
};

  const handlePasswordChange = (e) =>
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

const validateForm = () => {
  if (!formData.name.trim()) return "Name cannot be empty";
  if (!formData.email.trim() || !formData.email.includes("@"))
    return "Enter a valid email";
  if (!formData.phone.trim()) return "Contact Number cannot be empty";
  if (formData.phone.length !== 11)
    return "Contact Number must be exactly 11 digits";
  return null;
};

  const handleSave = async () => {
    const userId = getUserId();
    if (!userId) return showAppModal("User ID not found!");

    const validationError = validateForm();
    if (validationError) return showAppModal(`${validationError}`);

    try {
      setLoading(true);
      const res = await axios.put(
        `https://firetrace-capstone.onrender.com/api/users/${userId}`,
        { ...formData }
      );
      localStorage.setItem("user", JSON.stringify(res.data.user || res.data));
      showAppModal("Profile updated successfully!");
    } catch (err) {
      console.error("Update failed:", err);
      showAppModal("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const userId = getUserId();
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    setProfileImg(URL.createObjectURL(file));

    const formDataCloud = new FormData();
    formDataCloud.append("file", file);
    formDataCloud.append("upload_preset", UPLOAD_PRESET);

    try {
      setLoading(true);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: "POST", body: formDataCloud }
      );

      const data = await res.json();
      if (!res.ok || data.error)
        throw new Error(data.error?.message || "Cloudinary upload failed");

      const imageUrl = data.secure_url;

      if (userId) {
        await axios.put(`https://firetrace-capstone.onrender.com/api/users/${userId}`, {
          profileImage: imageUrl,
        });

        const userRes = await axios.get(`/api/users/${userId}`);
        const updatedUser = userRes.data;

        localStorage.setItem("user", JSON.stringify(updatedUser));

        window.location.reload();
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      showAppModal("Failed to upload image.");
      setProfileImg(profilePlaceholder);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword.trim()) return "Current password cannot be empty";
    if (!newPassword.trim()) return "New password cannot be empty";
    if (newPassword.length < 6)
      return "New password must be at least 6 characters";
    if (newPassword !== confirmPassword)
      return "New password and confirm password do not match";

    return null;
  };

  const handlePasswordSave = async () => {
    const userId = getUserId();
    if (!userId) return showAppModal("User ID not found!");

    const validationError = validatePassword();
    if (validationError) return showAppModal(`${validationError}`);

    try {
      setLoading(true);
      await axios.put(`https://firetrace-capstone.onrender.com/api/users/${userId}/password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      showAppModal("Password updated successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error("Password update failed:", err);
      showAppModal("Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
  <div className="settings-wrapper">
  <div className="settings-panel">
    <div className="profile-card" style={{ textAlign: "center" }}>
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => setActiveTab("personal")}
          >
            Personal Information
          </button>
          <button
            className={`tab ${activeTab === "password" ? "active" : ""}`}
            onClick={() => setActiveTab("password")}
          >
            Login & Password
          </button>
        </div>
      </div>

      {/* Profile Image */}
      <div className="profile-img-wrapper">
        <div className="profile-img">
          <img src={profileImg} alt="Profile" />
        </div>
        <label htmlFor="profileUpload" className="camera-icon">
          <img src={cameraIcon} alt="Camera" />
        </label>
        <input
          type="file"
          id="profileUpload"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
      </div>

      <h3 className="profile-name" style={{ marginTop: "16px" }}>
        {formData.name || "Your Name"}
      </h3>
    </div>
  </div>

  <main className="form-section1">
    <div className="form-container">
      {activeTab === "personal" ? (
        <>
          <div className="form-row">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Full Name"
            />
          </div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
          />
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Address"
          />
          <div className="form-row">
           <input
  type="tel"
  name="phone"
  value={formData.phone}
  onChange={(e) => {
    // Allow only numbers
    const value = e.target.value.replace(/[^0-9]/g, "");
    handleChange({ target: { name: "phone", value } });
  }}
  placeholder="Contact Number"
  minLength={11}
  maxLength={11}
  pattern="09[0-9]{9}"
  title="Phone number must be 11 digits and start with 09"
/>


          </div>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={loading}
          >
            Save Changes
          </button>
        </>
      ) : (
        <>
          <p className="subheading">Change Password</p>

          <div className="password-wrapper">
            <input
              type={showCurrent ? "text" : "password"}
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              placeholder="Enter Current Password"
            />
            <button
              type="button"
              className="eye-icon"
              onClick={() => setShowCurrent(prev => !prev)}
              tabIndex={-1}
            >
              {showCurrent ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
            </button>
          </div>

          <div className="password-wrapper">
            <input
              type={showNew ? "text" : "password"}
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              placeholder="Enter New Password"
            />
            <button
              type="button"
              className="eye-icon"
              onClick={() => setShowNew(prev => !prev)}
              tabIndex={-1}
            >
              {showNew ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
            </button>
          </div>

          <div className="password-wrapper">
            <input
              type={showConfirm ? "text" : "password"}
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              placeholder="Confirm New Password"
            />
            <button
              type="button"
              className="eye-icon"
              onClick={() => setShowConfirm(prev => !prev)}
              tabIndex={-1}
            >
              {showConfirm ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
            </button>
          </div>

          <button
            className="save-button"
            onClick={handlePasswordSave}
            disabled={loading}
          >
            Save Password
          </button>
        </>
      )}
    </div>
  </main>

  {/* ✅ Modal for messages */}
  {showModal && (
    <div
      className="modal-overlay0"
      onClick={() => setShowModal(false)}
    >
      <div
        className="modal-content0"
        onClick={(e) => e.stopPropagation()}
      >
        <p>{modalMessage}</p>
        <button className="close-btn0" onClick={() => setShowModal(false)}>
          Close
        </button>
      </div>
    </div>
  )}
</div>
  );}

export default Settings;
